<template>
  <div class="start">

    <send-sample-dialog :samples="EXAMPLES" />

    <start-header
      :media="{ id: 'b1a94da6-08ef-4a9e-85a0-4d0642348508', alt: 'Group e-cards', video: true }"
      :bg="{ src: 'https://cdn.ellacard.com/media/007ea327-b4b4-42cd-a372-870177de6d9e/lg.mp4', color: '#9A9C98' }"
      :to="{ name: 'catalog', hash: '#search' }"
      @click-laptop="global.lastSelectedType = 'GROUP'"
    >
      <template v-slot:h1>Group greeting cards from Ellacard</template>
      <template v-slot:h2>The best way to create and send personalized, memorable, group greeting cards.</template>
    </start-header>

    <start-layer clip-waves-class="light-gradient" :offset=".75">
      <div class="actions">
        <v-btn class="btn-pop" x-large rounded dark color="ec-blue" :to="{ name: 'catalog', hash: '#search' }" @click.capture="global.lastSelectedType = 'GROUP'">
          Start a group card
          <v-icon class="mr-n1" v-html="mdiChevronRight" />
        </v-btn>
      </div>

      <div class="grid-alternate">
        <template v-for="ex in EXAMPLES">
          <start-preview :to="{ hash: ex.hash }" :pages="ex.pages" :alt="ex.alt" />
          <div>
            <h4 class="mb-3"><v-icon v-html="ex.icon" /> {{ex.title}}</h4>
            <div class="mb-6" v-html="ex.body" />
            <v-btn class="btn-pop" rounded dark color="ec-blue" :title="ex.title + ' demo'" :to="{ hash: ex.hash }">
              Try demo…
            </v-btn>
          </div>
        </template>
      </div>

      <div class="actions">
        <v-btn class="btn-pop" x-large rounded dark color="ec-blue" :to="{ name: 'catalog', hash: '#search' }" @click.capture="global.lastSelectedType = 'GROUP'">
          Start a group card
          <v-icon class="mr-n1" v-html="mdiChevronRight" />
        </v-btn>
      </div>

    </start-layer>

    <start-layer clip-waves-class="gold-gradient" :offset=".9">
      <h2>How it works</h2>

      <div class="grid-alternate">

        <web-media
          class="d-block hover-pop rounded-xl white"
          aspect-ratio="4 / 3"
          id="4a0deb61-daa4-4d54-b79a-eebc1b966ed0"
          alt="Sample card designs splashing down on a wooden table"
          is-video
          :to="{ name: 'catalog', hash: '#search' }"
          @click.native="global.lastSelectedType = 'GROUP'"
        />

        <div>
          <h4 class="mb-3">Choose or create the perfect design</h4>
          <div class="mb-6">Start with one of our <router-link :to="{ name: 'catalog', hash: '#search' }" @click.capture="global.lastSelectedType = 'GROUP'">thousands of designs</router-link> for <router-link :to="{ name: 'catalog-categories', hash: '#search' }" @click.capture="global.lastSelectedType = 'GROUP'">every occasion</router-link>, or use our editing tools to <router-link :to="{ name: 'catalog', query: { q: '#design-your-own' }, hash: '#search' }" @click.capture="global.lastSelectedType = 'GROUP'">upload / create your own</router-link>.</div>
        </div>

        <web-media
          class="hover-pop rounded-xl white"
          aspect-ratio="4 / 3"
          id="607e1c42-594a-42d8-88dc-3b1c47c92641"
          alt="Using the Ellacard editor to personalize a card"
          is-video
        />

        <div>
          <h4 class="mb-3">Personalize your card</h4>
          <div>
            Add messages, pictures, videos, colors, backgrounds, music, confetti, and more. Ellacard's collaborative editing tools are unmatched and work perfectly on phones and computers.
          </div>
        </div>

        <web-media
          class="d-block hover-pop rounded-xl white"
          aspect-ratio="4 / 3"
          id="28aa786a-d55c-49d6-a894-0ac9ac9692f9"
          alt="Adding value to a group gift card"
          is-video
          :to="{ name: 'catalog-gift-cards', hash: '#search' }"
          @click.native="global.lastSelectedType = 'GROUP'"
        />

        <div>
          <h4 class="mb-3">Add a group gift card</h4>
          <div>
            Optionally add a group gift card from <router-link :to="{ name: 'catalog-gift-cards' }" @click.capture="global.lastSelectedType = 'GROUP'">thousands of international merchants</router-link>. Anyone can add value to it, and you can even choose to hide the total value from contributors.
          </div>
        </div>

        <div class="relative hover-pop-group" style="aspect-ratio: 8 / 7;">
          <div
            class="absolute rounded grey lighten-5 rn2 pa-3 overflow-hidden"
            style="width: 60%; left: 0; top: 0; max-height: 100%;"
          >
            <web-media
              class="elevation-3"
              aspect-ratio="166 / 362"
              id="132180a1-18b4-4720-bae6-2714d9a47d43"
              alt="Example email invitation to sign a group card on Ellacard"
              min="lg"
            />
          </div>
          <web-media
            class="absolute r3 has-alpha"
            style="width: 50%; top: 5%; right: 1%;"
            aspect-ratio="240 / 435"
            id="672bd30f-187a-4d8f-ba83-122b07bd4ba4"
            alt="Example text message invitation to sign a group card on Ellacard"
            min="lg"
          />
        </div>

        <div>
          <h4 class="mb-3">Invite contributors to sign</h4>
          <div>
            Share / post a link to sign the card, or use Ellacard to invite contributors via email / text. You can make personal messages from others private.
          </div>
        </div>

        <div class="relative hover-pop-group" style="aspect-ratio: 4 / 3;">
          <outline-laptop class="absolute has-alpha" style="width: 110%; left: -4%;">
            <web-media aspect-ratio="16 / 9" id="6477f405-c06e-4064-a5a3-a45ec556de3b" alt="Opening an Ellacard (landscape)" is-video />
          </outline-laptop>
          <outline-phone class="absolute has-alpha rn5" style="top: 10%; width: 32%; left: 0;" vertical>
            <web-media aspect-ratio="9 / 16" id="0938a4be-cc9d-4fd6-af8f-98c30f68ca30" alt="Opening an Ellacard (portrait)" is-video />
          </outline-phone>
          <web-media
            class="absolute rounded r3 white"
            style="width: 50%; bottom: -3%; right: 0;"
            aspect-ratio="640 / 504"
            id="5a0b7247-74cb-40de-8e47-1621af63c010"
            alt="Group card status page after sending"
          />
        </div>

        <div>
          <h4 class="mb-3">Choose how / when to send your card</h4>
          <div>
            Set a send date, and Ellacard will automatically send your card via email / text. Track delivery status and let your recipient reply with a message.
          </div>
        </div>

      </div>

      <div class="actions">
        <v-btn class="btn-pop" x-large rounded dark color="ec-blue" :to="{ name: 'catalog', hash: '#search' }" @click.capture="global.lastSelectedType = 'GROUP'">
          Start a group card
          <v-icon class="mr-n1" v-html="mdiChevronRight" />
        </v-btn>
      </div>
    </start-layer>

    <start-layer clip-waves-class="dark-gradient" :offset=".6">
      <h2>Business tools</h2>

      <div class="grid-alternate">

        <web-media
          class="d-block hover-pop rounded white"
          aspect-ratio="16 / 9"
          id="4c484036-9f33-4725-b6c1-0509f97a02ef"
          alt="Automation"
          is-video
          :to="{ name: 'start-automation' }"
        />

        <div>
          <h4 class="mb-3">Automation</h4>
          <div class="mb-6">Ellacard will automatically schedule group cards in advance, including inviting contributors to sign. Perfect for things like employee birthdays and work anniversaries.</div>
          <div class="ma-n2">
            <v-btn class="ma-2 btn-pop" rounded dark color="ec-blue" :to="{ name: 'batch-order' }">
              Create an automation <v-icon class="mr-n1" v-html="mdiChevronRight" />
            </v-btn>
            <v-btn class="ma-2 btn-pop" rounded dark color="ec-blue" title="More about automation tools" :to="{ name: 'start-automation' }">
              Learn more <v-icon class="mr-n1" v-html="mdiChevronRight" />
            </v-btn>
          </div>
        </div>

        <web-media
          class="d-block hover-pop rounded white"
          aspect-ratio="1"
          id="5a52ae1d-b5e9-4b51-856b-a4c93a527c1c"
          alt="Business tools / user activity"
          :to="{ name: 'start-recognition' }"
        />

        <div>
          <h4 class="mb-3">Shared managed accounts</h4>
          <div class="mb-6">
            Give your whole team access so anyone can send kudos / thanks for a job well done using your organization's credits. Collect detailed metrics on how your team is using Ellacard.
          </div>
          <div class="ma-n2">
            <v-btn class="ma-2 btn-pop" rounded dark color="ec-blue" :to="{ name: 'account-org' }">
              Create an organization <v-icon class="mr-n1" v-html="mdiChevronRight" />
            </v-btn>
            <v-btn class="ma-2 btn-pop" rounded dark color="ec-blue" title="More about employee recognition / rewards" :to="{ name: 'start-recognition' }">
              Learn more <v-icon class="mr-n1" v-html="mdiChevronRight" />
            </v-btn>
          </div>
        </div>

        <web-media
          class="d-block hover-pop rounded white"
          aspect-ratio="432 / 324"
          id="7ac8562f-ba12-478b-8f19-95830b40666c"
          alt="Bulk email campaign metrics"
          :to="{ name: 'start-bulk-email' }"
        />

        <div>
          <h4 class="mb-3">Bulk email</h4>
          <div class="mb-6">
            Use Ellacard to send your group card in a bulk email campaign to as many recipients as you want. Automatically handle unsubscribes and collect detailed metrics. Perfect to send out a corporate holiday card.
          </div>
          <div class="ma-n2">
            <v-btn class="ma-2 btn-pop" rounded dark color="ec-blue" :to="{ name: 'bulk-email' }">
              Create an email campaign <v-icon class="mr-n1" v-html="mdiChevronRight" />
            </v-btn>
            <v-btn class="ma-2 btn-pop" rounded dark color="ec-blue" title="More about bulk email" :to="{ name: 'start-bulk-email' }">
              Learn more <v-icon class="mr-n1" v-html="mdiChevronRight" />
            </v-btn>
          </div>
        </div>

        <web-media
          class="d-block hover-pop rounded white"
          aspect-ratio="16 / 9"
          id="74458356-0685-440d-8a14-bb7cd66aa31e"
          alt="Custom branding example"
          is-video
          :to="{ name: 'start-embedding-api' }"
        />

        <div>
          <h4 class="mb-3">Custom branding</h4>
          <div class="mb-6">
            Add your own logo and colors. Send cards from your own email address. Host Ellacard on your own site with our <router-link :to="{ name: 'start-embedding-api' }">embedding API</router-link> for a fully white-labelled experience.
          </div>
          <div class="ma-n2">
            <v-btn class="ma-2 btn-pop" rounded dark color="ec-blue" :to="{ name: 'account-org-branding' }">
              Set up custom branding <v-icon class="mr-n1" v-html="mdiChevronRight" />
            </v-btn>
            <v-btn class="ma-2 btn-pop" rounded dark color="ec-blue" title="More about embedding" :to="{ name: 'start-embedding-api' }">
              Learn more <v-icon class="mr-n1" v-html="mdiChevronRight" />
            </v-btn>
          </div>
        </div>

      </div>
    </start-layer>

    <start-reviews tag="GR" />

    <start-footer />

  </div>
</template>


<script>
import OutlineLaptop from '@/components/OutlineLaptop.vue';
import OutlinePhone from '@/components/OutlinePhone.vue';
import SendSampleDialog from '@/components/SendSampleDialog.vue';
import StartFooter from '@/components/StartFooter.vue';
import StartHeader from '@/components/StartHeader.vue';
import StartLayer from '@/components/StartLayer.vue';
import StartPreview from '@/components/StartPreview.vue';
import StartReviews from '@/components/StartReviews.vue';
import WebMedia from '@/components/WebMedia.vue';
import { mdiChevronRight, mdiDomain, mdiCakeVariant, mdiWalletGiftcard, mdiEmoticonSad } from '@mdi/js';


const EXAMPLES = [
  {
    icon: mdiDomain,
    title: 'Group cards for the office',
    body: 'Invite all your co-workers / colleagues to sign a group card. Sign cards with text, pictures, videos, GIFs, and more. Add automation to <a href="/start/automation">bulk-schedule cards in advance</a>.',
    alt: 'Work anniversary',
    pages: [
      {
        "jpg": "https://cdn.ellacard.com/r/f93ea0cd-ed50-41c1-9c9f-0453edae9365_270x378.jpg"
      },
      {
        "jpg": "https://cdn.ellacard.com/r/8f40e3c4-b236-4d46-a52a-28247a044ea0_270x378.jpg"
      },
      {
        "webp": "https://cdn.ellacard.com/r/a542e50e-9642-4f78-9989-34d51581a9de_270x378.webp",
        "mp4": "https://cdn.ellacard.com/r/a542e50e-9642-4f78-9989-34d51581a9de_270x378.mp4"
      }
    ],
    hash: '#demo-office',
    id: '64c9dff3-2ac5-47a3-ac87-a52d560fb76f'
  },
  {
    icon: mdiCakeVariant,
    title: 'Group cards for birthdays',
    body: 'Invite your friends and family to sign a birthday card. Add your own photos and videos. Unlimited contributors. Unlimited pages.',
    alt: 'Birthday',
    pages: [
      {
        "jpg": "https://cdn.ellacard.com/r/794b7a64-d3d7-4327-aae9-4e10dafd847e_270x378.jpg"
      },
      {
        "webp": "https://cdn.ellacard.com/r/ea70605a-635b-4a46-9b2e-e3972ea5b219_270x378.webp",
        "mp4": "https://cdn.ellacard.com/r/ea70605a-635b-4a46-9b2e-e3972ea5b219_270x378.mp4"
      },
      {
        "jpg": "https://cdn.ellacard.com/r/ff78b897-1cbf-48a1-b52e-e58583e953d2_270x378.jpg"
      }
    ],
    hash: '#demo-birthday',
    id: 'f1966960-7365-4c8f-a080-c1b287ac5f07'
  },
  {
    icon: mdiWalletGiftcard,
    title: 'Group gift cards',
    body: 'Add value to a single gift card, or add multiple gift cards from <a href="/gift-cards">thousands of international merchants</a>, like <a href="/gift-cards/RU:AMZ-US">Amazon</a>, <a href="/gift-cards/RU:SBUX-US">Starbucks</a>, and <a href="/gift-cards/RU:UBER-US">Uber</a>.',
    pages: [
        {
            "jpg": "https://cdn.ellacard.com/r/666b8ac8-8df6-4dce-a33a-20e43736cb56_270x378.jpg"
        },
        {
            "webp": "https://cdn.ellacard.com/r/212af83e-6aa1-46dc-9e33-01d1af31ddf4_270x378.webp",
            "mp4": "https://cdn.ellacard.com/r/212af83e-6aa1-46dc-9e33-01d1af31ddf4_270x378.mp4"
        },
        {
            "jpg": "https://cdn.ellacard.com/r/51c7fe52-c4f1-4ce6-aad4-e33268d38d16_270x378.jpg"
        }
    ],
    hash: '#demo-gift-cards',
    id: 'eba5f14d-57f9-4b27-afad-0288bc6b4fd2'
  },
  {
    icon: mdiEmoticonSad,
    title: 'Group sympathy cards',
    body: 'Start healing with messages of condolences and sympathy from friends, family, and co-workers. Keep your heartfelt messages, photos, and videos private from other contributors.',
    pages: [
      {
        "webp": "https://cdn.ellacard.com/r/9729ced1-991f-45f5-b040-e29e066484c1_270x378.webp",
        "mp4": "https://cdn.ellacard.com/r/9729ced1-991f-45f5-b040-e29e066484c1_270x378.mp4"
      },
      {
        "webp": "https://cdn.ellacard.com/media/78996058-3b3e-47cb-a8ec-45a55ff2f943/sm.webp"
      },
      {
        "webp": "https://cdn.ellacard.com/media/62361bcd-c3ea-4009-abb9-bef6443401ab/sm.webp"
      }
    ],
    hash: '#demo-sympathy',
    id: '6166ec5a-0e10-41ba-9f60-a7ddd57f15b1'
  }
];


export default {
  name: 'start-group-cards',

  routerViewMeta: {
    $class: 'fade-transparent',
    $subtitle: 'Group cards',
    description: 'The best way to create and send personalized, memorable, group greeting cards.'
  },

  components: {
    OutlineLaptop,
    OutlinePhone,
    SendSampleDialog,
    StartFooter,
    StartHeader,
    StartLayer,
    StartPreview,
    StartReviews,
    WebMedia
  },

  created() {
    Object.assign(this, { EXAMPLES, mdiChevronRight });
  },

  mounted() {
    window.GLOBAL.lastSelectedType = 'GROUP';
  }
}
</script>
